/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import makeStyles from '@mui/styles/makeStyles';
import { FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDeviceDetect } from 'hooks';
import { EveningIcon, AfternoonIcon, MorningIcon } from 'components/elements/Icons';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function AvailabilitySelector({ onChange, label: formLabel, helperText, error, value, isMorningEnabled, isAfternoonEnabled, isEveningEnabled }) {
  const classes = useStyles();
  const { isDesktop } = useDeviceDetect();
  const { t, i18n } = useTranslation('labels');
  const checkboxesGroups = useMemo(() => {
    const groups = [];
    if (isMorningEnabled) {
      groups.push({ id: 'morning', label: t('morning'), time: t('morning_timing'), image: <MorningIcon />, enabled: isMorningEnabled });
    }
    if (isAfternoonEnabled) {
      groups.push({ id: 'afternoon', label: t('afternoon'), time: t('afternoon_timing'), image: <AfternoonIcon />, enabled: isAfternoonEnabled });
    }
    if (isEveningEnabled) {
      groups.push({ id: 'evening', label: t('evening'), time: t('evening_timing'), image: <EveningIcon />, enabled: isEveningEnabled });
    }
    return groups;
  }, [i18n.language, isMorningEnabled, isAfternoonEnabled, isEveningEnabled]);

  const days = useMemo(
    () => [
      { id: 0, label: t('sunday') },
      { id: 1, label: t('monday') },
      { id: 2, label: t('tuesday') },
      { id: 3, label: t('wednesday') },
      { id: 4, label: t('thursday') },
      { id: 5, label: t('friday') },
      { id: 6, label: t('saturday') },
    ],
    [i18n.language],
  );

  const getLabel = (day) => (!isDesktop ? day.substr(0, 1) : day);

  // eslint-disable-next-line react/no-unstable-nested-components
  function Labels() {
    return (
      <div className={classes.labels}>
        {days.map((day) => (
          <span key={day.id}>{getLabel(day.label.substr(0, 3).toLocaleUpperCase())}</span>
        ))}
      </div>
    );
  }

  return (
    <div className={classes.availabilitySelectorWrapper}>
      <FormControl component="fieldset" className={classes.formControl} error={error}>
        <FormLabel component="legend">{formLabel}</FormLabel>
        <FormGroup className={classes.availabilitySelector}>
          <Labels />
          {checkboxesGroups.map(({ id, label, image, time, enabled }) => (
            <div className={classes.availabilitySelectorGroup} key={id}>
              <div className={classes.groupLabel}>
                <div className={classes.icon}>{image}</div>
                <div className={classes.timeInfo}>
                  <span>{label}</span>
                  <span>({time})</span>
                </div>
              </div>
              <div className={classes.checkboxes}>
                {days.map((day) => (
                  <FormControlLabel
                    label=""
                    control={
                      <Checkbox
                        data-testid={`checkbox_${day.id}_${id}`}
                        disabled={!enabled}
                        classes={{ root: classes.input }}
                        name={`${day.id}`}
                        checked={value[id]?.includes(day.id)}
                        color="primary"
                        role="checkbox"
                        onChange={(e) => {
                          const optionValue = day.id;
                          const newValue = {
                            morning: [...value.morning],
                            afternoon: [...value.afternoon],
                            evening: [...value.evening],
                          };
                          if (e.target.checked) {
                            newValue[id].push(optionValue);
                          } else {
                            newValue[id] = value[id].filter((item) => item !== optionValue);
                          }
                          onChange(newValue);
                        }}
                      />
                    }
                    key={day.id}
                  />
                ))}
              </div>
            </div>
          ))}
        </FormGroup>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    </div>
  );
}

AvailabilitySelector.propTypes = {
  helperText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({
    morning: PropTypes.arrayOf(PropTypes.number),
    afternoon: PropTypes.arrayOf(PropTypes.number),
    evening: PropTypes.arrayOf(PropTypes.number),
  }),
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
  isMorningEnabled: PropTypes.bool,
  isAfternoonEnabled: PropTypes.bool,
  isEveningEnabled: PropTypes.bool,
  error: PropTypes.bool,
};

AvailabilitySelector.defaultProps = {
  helperText: '',
  isMorningEnabled: true,
  isAfternoonEnabled: true,
  isEveningEnabled: true,
  error: false,
  value: {
    morning: [],
    afternoon: [],
    evening: [],
  },
};

export default AvailabilitySelector;
